define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/when",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/def",
	"csi/def/ActionFactory",
	"csi/def/ViewRef",
	"csi/html",
	"csi/html/Rule",
	"csi/locale",
	"csi/reflection",
	"csi/util"
], function(declare, domClass, when, Deferred, _, def, ActionFactory, ViewRef, html, Rule, locale, reflection, util) {

	/**
	 * Create intersection observer for child view so that when title is visible (scrolled into view) the child view will load
	 */
	function createIntersectionObserver(div, childView) {
		const observer = new IntersectionObserver((entries, observer) => {
			if (entries[0].isIntersecting) {
				observer.disconnect();
				childView.suspendedUntilScrolledIntoView = false;

				when(childView.suspendView(false), () => {
					childView.set("disabled", false);
					childView.resize();
				});
			}
		});

		observer.observe(div);

		// remove observer when child view is destroyed
		childView.own({
			remove: () => {
				observer.disconnect();
			}
		});
	}

	return declare(ViewRef, {
		// String: title for inserted view
		m_name: null,

		// Boolean: indicate that this object is a ViewRef
		isViewRef: true,

		// String: label to display in preference view dialog
		prefViewName: null,

		// Object: parameters to add to view before creating
		//prefViewParams: null,

		// Class: target class for view
		//rcTarget: null,

		constructor: function(xn) {
			var target = util.getProperty(xn, "Target");
			if (target) {
				this.rcTarget = reflection.getClass(target);
			}

			this.bReadOnly = util.getBooleanProperty(xn, "ReadOnly");
			this.m_bSlim = util.getBooleanProperty(xn, "Slim");
			this.m_classDef = util.getProperty(xn, "Class");
			this.m_rules = util.getDefs(xn, "Rule", Rule);
			this.idResource = util.getProperty(xn, "Name");
			this.prefViewName = util.getProperty(xn, "CustomViewName");
			this.switchboard = ActionFactory.getActionDef(xn, "Switchboard");
		},

		getQueryPath: function() {
			return this.m_path;
		},

		getReferencedClass: function(/*Class*/rc, /*Node?*/node) {
			// summary: if target already specified, then return it
			return this.rcTarget || this.inherited(arguments);
		},

		getReferencedViewDef: function(/*Node?*/node) {
			// summary: return view definition based on given node
			var defView;
			var rc = this.rcTarget;
			if (rc) {
				defView = def.getClassViewDef(rc, this.m_id, true);
			} else if (node) {
				defView = def.getNodeViewDef(node, this.m_id, true);
			}

			return defView;
		},

		resolve: function(/*LocaleResolver*/lr, /*Class*/rcRoot, /*Node*/nodeRoot) {
			// summary: get name if overridden by view - if none, then view name will be used when created
			if (!this.m_name) {
				this.m_name = this.idResource ? lr.getResource(this.idResource) : lr.getViewResource(this.key);
			}

			if (this.prefViewName) {
				this.prefViewName = lr.getResource(this.prefViewName);
			}

			if (lr.inConfigurationMode()) {
				// if view is being resolved as part of configuration UI, then resolve referenced view name now;
				// otherwise wait to do it until view is created
				var rc = this.getReferencedClass(rcRoot, nodeRoot);
				if (rc) {
					this.m_name = lr.getTypeView(rc.getType(), this.m_id);
				}
			}

			if (this.switchboard) {
				this.switchboard.resolve(lr, rcRoot, nodeRoot);
			}
		},

		keyToHtml: function(/*Context*/ctx, /*DOMNode*/domNode) {
			// summary: return callback to create view reference
			const parentView = ctx.view;
			var bCustomView = !ctx.bPrint && parentView.supportsCustomViews();
			var urlRoot = this.prefViewParams && this.prefViewParams.urlRoot;
			var nodeRoot = urlRoot ? ctx.cache.getNode(urlRoot) : ctx.getNode();
			var node = this.getReferencedNode(ctx.cache, nodeRoot);
			var rc = this.rcTarget || reflection.getNodeClass(node);
			var bRuledOut = false;
			var defView;

			// if referenced node is not known and in composite custom view, then determine referenced class to generate view
			if (!rc) {
				var rcRoot = reflection.getNodeClass(nodeRoot) || ctx.defView.getRootClass();
				rc = this.getReferencedClass(rcRoot, nodeRoot);
			}

			// if referenced node is known, check rules; if not passed, then reset to null
			if (node && this.m_rules && !_.every(this.m_rules, function(r) {return r.passes(r.bFromRoot ? nodeRoot : node, ctx.cache, ctx);})) {
				// still create view when it fails rules if in custom view
				if (bCustomView) {
					bRuledOut = true;
				} else {
					node = null;
				}
			}

			if (node && !this.rcTarget) {
				// find view based on node instance
				defView = def.getNodeViewDef(node, this.m_id, true);
			} else if (rc && (this.rcTarget || bCustomView)) {
				// find view based on class
				defView = def.getClassViewDef(rc, this.m_id, true);
			}

			if (!defView) {
				html.destroyElement(domNode);
				return null;
			}

			const labelResource = html.getDataAttribute(domNode, html.DATA_TEMPLATE_LABEL);
			let name = this.m_name;

			if (labelResource) {
				name = locale.parameterResource(labelResource);
			} else if (name == null) {
				name = locale.getResolver({typeContext: rc.getType()}).getTypeView(rc.getType(), this.m_id);
			}

			// set view to read-only/slim if overridden here
			if (this.bReadOnly) {
				defView.setReadOnly(true);
			}

			if (this.m_bSlim) {
				defView.m_bSlim = this.m_bSlim;
			}

			if (this.m_classDef) {
				domClass.add(domNode, this.m_classDef);
			}

			// setup parameters for view - capture now so that this definition could potentially be used multiple times
			const params = {
				compositeCustomViewName: this.prefViewName || name,
				...this.prefViewParams
			};

			// create header for view if named; if slim view (data sheets), then hide label if no label resource
			const hideLabel = ctx.defView.isSlim(true) && (labelResource == null);
			let titleDiv;

			if (ctx.defView.bShowLabels && name && !hideLabel) {
				this.m_name = name;

				// support key on TR, rather than having to put on TD - this allows for entire TR to be deleted rather than just TD
				const dom2 = (domNode.tagName.toLowerCase() == "tr") ? domNode.lastChild : null;

				titleDiv = html.create("div", {
					"class": "csi-view-title csi-view-title-" + defView.getHtmlKey(),
					innerText: name
				}, dom2 || domNode, "first");

				// start any views out as suspended until they are scrolled into view
				if (!ctx.bPrint && defView.getPageRelationship() && (parentView.defView.getViewRefs().length > 2)) {
					params.suspendedUntilScrolledIntoView = true;
				}
			}

			// if switchboard, then preserve current node for context; otherwise, target is used to show view without context,
			// so providing node as context would be incorrect
			if (this.rcTarget && !this.switchboard) {
				node = null;
			}

			// return callback function to be performed later
			return async () => {
				const childView = await this._processView(ctx, domNode, defView, node, params, bRuledOut);

				if (titleDiv && childView?.suspendedUntilScrolledIntoView) {
					when(parentView.getDeferred(), () => {
						createIntersectionObserver(titleDiv, childView);
					});
				}
			};
		},

		/**
		 * Insert view - store key for view which will help determine sort order for nested views
		 * @param {Context} ctx 
		 * @param {HTMLElement} domNode 
		 * @param {View} defView 
		 * @param {C8Node} node 
		 * @param {Record<string, any>} params 
		 * @param {boolean} bRuledOut 
		 * @return {Promise<ViewWidget>}
		 */
		_processView(ctx, domNode, defView, node, params, bRuledOut) {
			const dfd = new Deferred();

			ctx.view._insertView(defView, node, params).then((view) => {
				if (!view) {
					dfd.resolve();
					return null;
				}

				if (!ctx.view.setNestedViewDefault(view, this, ctx) || bRuledOut) {
					if (ctx.bPrint) {
						// if printing, then destroy view as it is not visible
						view.destroyView();
						html.destroyElement(domNode);
						dfd.resolve();
						return null;
					} else {
						// nested view is hidden by custom view, so create stub view and mark as already started so it will not load as normal;
						// do not destroy either as all child views are needed to edit custom views of Html view
						html.setVisible(domNode, false);
						view._started = true;
					}
				}

				if (this.switchboard) {
					view.addSwitchboard(this.switchboard);
				}

				// support for key on TR
				const dom2 = (domNode.tagName.toLowerCase() == "tr") ? domNode.lastChild : null;
				view.placeAt(dom2 || domNode);

				// only resolve deferred (move to next view) once this view has finished loading
				when(view.startup(), () => {
					// destroy empty views from DOM when printing - only do so for views directly under Html print view, not for further nested views (i.e. Canvas Comments)
					if (ctx.bPrint && ctx.view.bPrintView && view.defView.isSlim() && view.isValidView() && view.getViewNode() && !view.hasContent()) {
						domNode.parentNode.removeChild(domNode);
						view.viewParent = null;

						// defer destroy so that view can finish up
						setTimeout(() => {
							view.destroyView();
						}, 1);

						dfd.resolve(null);
					} else {
						dfd.resolve(view);
					}
				});
			});

			return dfd;
		}
	});
});